import { Box, SxProps, Tab, Tabs, Theme } from '@mui/material';
import { ReactNode, useState } from 'react';

interface TabPanelProps {
 children: ReactNode;
 index: number;
 value: number;
 keepMounted?: boolean;
}
interface TabItemProps {
 id: string;
 label: string;
 content: ReactNode;
 disabled?: boolean;
}
interface XTabsProps {
 tabs: TabItemProps[];
 defaultTab?: number;
 variant?: 'fullWidth' | 'standard' | 'scrollable';
 ariaLabel?: string;
 sx?: SxProps<Theme>;
 tabSx?: SxProps<Theme>;
 panelSx?: SxProps<Theme>;
 wrapSx?: SxProps<Theme>;
 onChange?: (newValue: number) => void;
 keepMounted?: boolean;
}

function a11yProps(index: number) {
 return {
  id: `xtabs-tab-${index}`,
  'aria-controls': `xtabs-tabpanel-${index}`,
 };
}

function CustomTabPanel(props: TabPanelProps) {
 const { children, value, index, keepMounted = false, ...other } = props;
 const isSelected = value === index;

 return (
  <Box
   role="tabpanel"
   id={`xtabs-tabpanel-${index}`}
   aria-labelledby={`xtabs-tab-${index}`}
   aria-hidden={!isSelected}
   sx={{
    flex: isSelected ? 1 : 0,
    minHeight: 0,
    flexDirection: 'column',
    overflow: 'hidden',
    display: isSelected ? 'flex' : 'none',
   }}
   {...other}
  >
   {(keepMounted || isSelected) && (
    <Box
     sx={{
      p: '20px',
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
     }}
    >
     {children}
    </Box>
   )}
  </Box>
 );
}

const defaultTabSx = {
 '& .Mui-selected': {
  backgroundColor: (theme: Theme) => theme.palette.kemek.white,
 },
 '& .MuiButtonBase-root.MuiTab-root': {
  pt: '12px',
  pb: '12px',
  fontSize: '14px',
  fontWeight: 400,
  '&[aria-selected="false"]': {
   color: (theme: Theme) => theme.palette.kemek.darkGray,
  },
  '&[aria-selected="true"]': {
   color: (theme: Theme) => theme.palette.kemek.blue,
  },
 },
 '& .MuiTabs-indicator': {
  height: '1px',
 },
};

const defaultPanelSx = {
 p: 0,
};

const XTabs = ({
 tabs,
 defaultTab = 0,
 variant = 'fullWidth',
 ariaLabel = 'Tabs',
 sx,
 tabSx,
 panelSx,
 wrapSx,
 onChange,
 keepMounted = false,
}: XTabsProps) => {
 const [tab, setTab] = useState(defaultTab);

 const handleChange = (event: React.SyntheticEvent, newValue: number) => {
  setTab(newValue);
  onChange?.(newValue);
 };

 return (
  <Box sx={wrapSx}>
   <Box
    sx={{
     borderBottom: 1,
     borderBottomColor: (theme: Theme) => theme.palette.kemek.lightestGray,
     mt: '-1px',
     flexShrink: 0,
     ...sx,
    }}
   >
    <Tabs
     variant={variant}
     value={tab}
     onChange={handleChange}
     aria-label={ariaLabel}
     sx={{
      ...defaultTabSx,
      ...tabSx,
     }}
    >
     {tabs.map((tabItem, index) => (
      <Tab
       key={tabItem.id}
       label={tabItem.label}
       disabled={tabItem.disabled}
       {...a11yProps(index)}
      />
     ))}
    </Tabs>
   </Box>

   {tabs.map((tabItem, index) => (
    <CustomTabPanel
     key={tabItem.id}
     value={tab}
     index={index}
     keepMounted={keepMounted}
    >
     <Box sx={{ ...defaultPanelSx, ...panelSx }}>{tabItem.content}</Box>
    </CustomTabPanel>
   ))}
  </Box>
 );
};

export default XTabs;
